import { Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { CurrencyPipe, getCurrencySymbol } from '@angular/common';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';

import { Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { AuthService } from '@core/auth/auth.service';
import { SharedModule } from '@shared/modules';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'net-input-money',
  templateUrl: './input-money.component.html',
  styleUrls: ['./input-money.component.scss'],
  providers: [
    CurrencyPipe,
    {
      provide: MatFormFieldControl,
      useExisting: InputMoneyComponent
    },
    provideNgxMask()
  ],
  standalone: true,
  imports: [
    SharedModule,
    NgxMaskDirective
  ]
})
export class InputMoneyComponent implements OnInit, OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<number> {

  static nextId = 0;

  @Input() allowDecimal = true;

  amount: UntypedFormControl = new UntypedFormControl(null);

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'input-money';
  describedBy = '';
  stateChanges = new Subject<void>();


  @Input() readonly = false;

  @Input()
  get currency(): string { return this._currency; }
  set currency(value: string) {
    this._currency = value;
    this.onChange(this.value);
  }
  private _currency: string;

  @Input()
  get currencyCode(): string { return this._currencyCode; }
  set currencyCode(value: string) {
    this._currencyCode = value;

    if (value) {
      this.currency = getCurrencySymbol(value, 'narrow');
    }
  }
  private _currencyCode: string;

  @Input()
  get decimalDigitCount(): number { return this._decimalDigitCount; }
  set decimalDigitCount(value: number) {
    this._decimalDigitCount = value;
    this.mask = `separator.${this._decimalDigitCount}`;
    this.stateChanges.next();
  }
  private _decimalDigitCount = 2;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder = '';

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @HostBinding() id = `input-money-${InputMoneyComponent.nextId++}`;
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input() value: number;

  get empty() {
    return this.value === null || this.value === undefined;
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value);

    if (this._disabled) {
      this.amount.disable();
    } else {
      this.amount.enable();
    }

    this.stateChanges.next();
  }
  private _disabled = false;

  mask = `separator.${this.decimalDigitCount}`;

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private currencyPipe: CurrencyPipe,
    public authService: AuthService,
    @Optional() @Self() public ngControl: NgControl
  ) {
    // Material form field implementation
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.amount.valueChanges
      .pipe(startWith(this.amount.value))
      .subscribe(value => {
        this.value = value;
        this.onChange(this.value);
      });
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this._elementRef.nativeElement.querySelector('input').focus();
    }
  }

  writeValue(value: number) {
    this.value = value;
    this.amount.setValue(value);
  }

  onChange = (_: any) => { };
  onTouched = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');

  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }
}
